import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from 'react-router-dom';
import ResizableTable from "../../ResizableTable";
import BulkEditModal from "./BulkEditModal";
import ChangeIndicator from "../ui/ChangeIndicator";
import LastModified from "../ui/LastModified";
import useRecordChanges from "../../hooks/useRecordChanges";
import {
  ArrowUp,
  ArrowDown,
  Copy,
  RefreshCw,
  AlertCircle,
  Clipboard,
  MessageSquare,
  RotateCcw,
  Save,
  X,
  Check,
  Edit3
} from "lucide-react";
import {
  doc,
  getDoc,
  updateDoc
} from "firebase/firestore";
import { db } from "../../firebase"; // Add this import for the Firestore database instance

/**
 * SalesTable component for displaying sales with expiration highlighting
 *
 * @param {object} props - Component props
 * @param {Array} props.data - Sales data to display
 * @param {Array} props.columns - Column definitions
 * @param {object} props.visibleColumns - Visibility state for columns
 * @param {object} props.sortDirection - Sort direction state
 * @param {function} props.toggleSort - Function to toggle sort direction
 * @param {function} props.calculateExpirationDays - Function to calculate days until expiration
 * @param {function} props.handleEdit - Function to handle edit action
 * @param {function} props.handleDelete - Function to handle delete action
 * @param {function} props.handleDuplicate - Function to initiate duplication
 * @param {function} props.handleRenew - Function to initiate renewal
 * @param {function} props.openNoteModal - Function to open note modal
 * @param {object} props.renewalPrices - Renewal prices from Settings
 * @param {number} props.renewalPricesVersion - Version number (timestamp) for renewal prices
 */
const SalesTable = ({
  data = [],
  columns = [],
  visibleColumns = {},
  sortDirection = {},
  toggleSort,
  calculateExpirationDays,
  handleEdit,
  handleDelete,
  handleDuplicate,
  handleRenew,
  openNoteModal,
  noRenewalTransactions,
  onNoRenewalToggle,
  showNoRenewalRecords,
  renewedTransactions,
  onRenewedToggle,
  showRenewedRecords,
  isSaving = false,
  renewalPrices = {}, // Prices directly from Settings
  renewalPricesVersion = 0, // Version/timestamp for tracking updates
  products = [], // Product information
  refreshData, // Function to refresh data after bulk updates
  handleBulkUpdate, // Function to handle bulk updates with local-first approach
}) => {
  // Add navigation hook
  const navigate = useNavigate();
  
  const [copiedId, setCopiedId] = useState(null);

  // Bulk edit state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  // State for tracking which rows are expanded for quick editing
  const [expandedRows, setExpandedRows] = useState({});
  // State for tracking edits in the quick edit form
  const [quickEditData, setQuickEditData] = useState({});
  // State for tracking which row is being edited in a popup
  const [editingRowId, setEditingRowId] = useState(null);
  const [showEditPopup, setShowEditPopup] = useState(false);

  // Hook to track record changes and last modified timestamps
  const { 
    getLastModifiedForRecord
  } = useRecordChanges(data, 'sales', true);

  // Function to handle edit button click - show popup instead of expanding rows
  const handleEditButtonClick = (transactionId, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Find the row data
    const rowData = data.find((r) => r.transactionId === transactionId);
    console.log("Found row data for editing:", rowData);

        if (rowData) {
      // Update quickEditData state with the row data
      setQuickEditData((prev) => ({
        ...prev,
            [transactionId]: { ...rowData },
          }));

      // Set the editing row ID
      setEditingRowId(transactionId);

      // Show the popup
      setShowEditPopup(true);
    } else {
      console.error("No row data found for transactionId:", transactionId);
    }
  };

  // Function to close the edit popup
  const closeEditPopup = () => {
    setShowEditPopup(false);
    setEditingRowId(null);
  };

  // Save changes from the popup
  const savePopupEdit = async (transactionId) => {
    try {
      if (quickEditData[transactionId]) {
        // Add _isQuickEdit flag to indicate this is from quick edit
        const editedData = {
          ...quickEditData[transactionId],
          _isQuickEdit: true,
        };

        // Call the same handleEdit function that the main edit button uses
        await handleEdit(editedData);

        // Close the popup
        closeEditPopup();

        // Clean up the quick edit data
        setQuickEditData((prev) => {
          const newState = { ...prev };
          delete newState[transactionId];
          return newState;
        });
      }
    } catch (error) {
      console.error("Error saving edits:", error);
      alert(`Failed to save changes: ${error.message}`);
    }
  };

  // Cancel edit in popup
  const cancelPopupEdit = () => {
    closeEditPopup();

    // Clean up the quick edit data
    if (editingRowId) {
      setQuickEditData((prev) => {
        const newState = { ...prev };
        delete newState[editingRowId];
      return newState;
    });
    }
  };

  // Handle changes in quick edit form
  const handleQuickEditChange = (transactionId, field, value) => {
    console.log(`Updating ${field} for ${transactionId} to:`, value);

    // Use functional update to prevent stale closures
    setQuickEditData((prev) => {
      const newData = { ...prev };
      if (!newData[transactionId]) {
        newData[transactionId] = {};
      }
      newData[transactionId] = { ...newData[transactionId], [field]: value };
      return newData;
    });
  };

  // Save quick edit changes
  const saveQuickEdit = async (transactionId) => {
    try {
      if (quickEditData[transactionId]) {
        // Add _isQuickEdit flag to indicate this is from quick edit
        const editedData = {
          ...quickEditData[transactionId],
          _isQuickEdit: true,
        };

        // Call the same handleEdit function that the main edit button uses
        // This will update Firebase and refresh the data
        await handleEdit(editedData);

        // Close the expanded row after save
        setExpandedRows((prev) => {
          const newState = { ...prev };
          delete newState[transactionId];
          return newState;
        });

        // Clean up the quick edit data
        setQuickEditData((prev) => {
          const newState = { ...prev };
          delete newState[transactionId];
          return newState;
        });
      }
    } catch (error) {
      console.error("Error saving quick edit:", error);
      alert(`Failed to save changes: ${error.message}`);
    }
  };

  // Cancel quick edit
  const cancelQuickEdit = (transactionId) => {
    // Close the expanded row
    setExpandedRows((prev) => {
      const newState = { ...prev };
      delete newState[transactionId];
      return newState;
    });

    // Clean up the quick edit data
    setQuickEditData((prev) => {
      const newState = { ...prev };
      delete newState[transactionId];
      return newState;
    });
  };

  // State for renewal message template loading flags
  // eslint-disable-next-line no-unused-vars
  const [renewalMessageTemplate, setRenewalMessageTemplate] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [templateLastUpdated, setTemplateLastUpdated] = useState(0);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [isExpireTemplateLoading, setIsExpireTemplateLoading] = useState(false);

  // Constants for localStorage
  const TEMPLATE_STORAGE_KEY = "renewal_message_template";
  const TEMPLATE_TIMESTAMP_KEY = "renewal_message_template_timestamp";
  const CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutes in milliseconds

  // State for Facebook business URL from settings
  const [facebookBusinessUrl] = useState("");

  // Load discounted prices, message template, and messaging settings from Settings
  useEffect(() => {
    // Initialize Firebase settings
    const db = window.firebase ? window.firebase.firestore() : null;
    if (db) {
      // Disable persistence to avoid caching issues
      db.settings({
        cacheSizeBytes: 0, // Disable caching
      });
      console.log("Firebase caching disabled for Settings collection");
    }
  }, []);

  // Your existing useEffect for loading settings
  useEffect(() => {
    const loadSettings = async () => {
      // Your existing code...
    };

    loadSettings();

    // Set up real-time listener for renewal message updates
    const db = window.firebase ? window.firebase.firestore() : null;
    if (db) {
      console.log("Setting up renewal message real-time listener...");

      // Force a cache clear for the renewalMessage document
      db.collection("Settings")
        .doc("renewalMessage")
        .get({ source: "server" })
        .then((doc) => {
          if (doc.exists) {
            console.log(
              "Initial server fetch complete. Template:",
              doc.data().template
            );
          }
        })
        .catch((err) => console.error("Error in initial server fetch:", err));

      const unsubscribe = db
        .collection("Settings")
        .doc("renewalMessage")
        .onSnapshot(
          { includeMetadataChanges: true }, // This ensures we get all updates, even metadata-only
          (doc) => {
            console.log(
              "Snapshot update received, source:",
              doc.metadata.fromCache ? "cache" : "server"
            );
            console.log("Has pending writes:", doc.metadata.hasPendingWrites);

            if (doc.exists && doc.data() && doc.data().template) {
              const template = doc.data().template;
              console.log(
                "⭐ Real-time update for renewal template:",
                template
              );
              console.log("Template length:", template.length);
              setRenewalMessageTemplate(template);
              setTemplateLastUpdated(Date.now());
            } else {
              console.warn("Received empty or invalid template in snapshot");
            }
          },
          (error) => {
            console.error("Error listening to renewal message updates:", error);
          }
        );

      // Clean up listener when component unmounts
      return () => {
        console.log("Cleaning up renewal message listener");
        unsubscribe();
      };
    }
  }, []);

  // Refresh settings when component becomes visible or after a period of time
  useEffect(() => {
    const refreshSettings = async () => {
      try {
        const db = window.firebase ? window.firebase.firestore() : null;
        if (db) {
          // Specifically refresh the renewal message template
          const messageDoc = await db
            .collection("Settings")
            .doc("renewalMessage")
            .get({ source: "server" }); // Force server fetch, bypass cache

          if (messageDoc.exists && messageDoc.data().template) {
            console.log(
              "Refreshed renewal template:",
              messageDoc.data().template
            );
            setRenewalMessageTemplate(messageDoc.data().template);
            setTemplateLastUpdated(Date.now());
          }
        }
      } catch (error) {
        console.error("Error refreshing settings:", error);
      }
    };

    // Refresh on mount and when products change
    refreshSettings();

    // Set up an interval to periodically refresh settings
    const intervalId = setInterval(refreshSettings, 60000); // Refresh every minute

    return () => clearInterval(intervalId);
  }, [products]);

  // Load template from localStorage or fetch it fresh
  const getTemplate = async () => {
    try {
      setIsTemplateLoading(true);

      // First try to get from localStorage
      const storedTemplate = localStorage.getItem(TEMPLATE_STORAGE_KEY);
      const storedTimestamp = localStorage.getItem(TEMPLATE_TIMESTAMP_KEY);

      if (storedTemplate && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp);
        const now = Date.now();

        // If the template is still fresh (less than 5 minutes old)
        if (now - timestamp < CACHE_MAX_AGE) {
          console.log(
            "Using localStorage template, age:",
            (now - timestamp) / 1000,
            "seconds"
          );
          setRenewalMessageTemplate(storedTemplate);
          setTemplateLastUpdated(timestamp);
          setIsTemplateLoading(false);
          return storedTemplate;
        }
      }

      // If not in localStorage or too old, fetch fresh
      const freshTemplate = await forceRefreshRenewalTemplate();

      // Store in localStorage for next time
      if (freshTemplate) {
        localStorage.setItem(TEMPLATE_STORAGE_KEY, freshTemplate);
        localStorage.setItem(TEMPLATE_TIMESTAMP_KEY, Date.now().toString());
      }

      setIsTemplateLoading(false);
      return freshTemplate;
    } catch (error) {
      console.error("Error getting template:", error);
      setIsTemplateLoading(false);
      return null;
    }
  };

  // Generate renewal message in Arabic using the template
  // eslint-disable-next-line no-unused-vars
  const generateRenewalMessage = async (sale) => {
    try {
      // Always force a fresh template fetch from the server first
      console.log("Fetching latest template for:", sale.clientName);
      const freshTemplate = await getTemplate();

      // Use the freshly fetched template or fall back to a default
      const defaultTemplate = `السلام عليكم 
  اهلا بحضرتك 
  باقي على انتهاء رخصة {productName} 
  يوم {days} 
  استفد من خصم التجديد المبكر {discountedPrice}
  بدلا من {currentPrice}`;

      // If we don't have a valid template from the server, use the default
      let templateToUse = freshTemplate || defaultTemplate;
      console.log("Template being used:", templateToUse);

      // Try to get product price as a fallback
      const product = products.find((p) => p.name === sale.productName);
      const productPrice = product ? product.price : sale.price || 0;

      // Initialize with product price in case we need to fall back
      let currentPrice = productPrice;
      let discountedPrice = Math.round(productPrice * 0.9); // Default 10% discount

      // IMPORTANT: Prioritize using the renewal prices from props
      if (renewalPrices && renewalPrices[sale.productName]) {
        console.log(
          `✅ Using renewal prices from props for ${sale.productName}:`,
          renewalPrices[sale.productName]
        );

        // Use settings prices if available
        currentPrice =
          renewalPrices[sale.productName].currentPrice || currentPrice;
        discountedPrice =
          renewalPrices[sale.productName].discountedPrice || discountedPrice;
      } else {
        console.log(
          `⚠️ No renewal prices found for ${sale.productName} in props, using default price calculation`
        );
        console.log(
          `ℹ️ Using product price for ${sale.productName}: ${currentPrice} / ${discountedPrice}`
        );
      }

      const days = calculateExpirationDays(sale.date, sale.subscriptionPeriod);

      // Process the template by replacing all placeholders
      let message = templateToUse;

      // Replace product name
      message = message.replace(/{productName}/g, sale.productName || "");

      // Handle days which could be a special string
      if (typeof days === "number") {
        message = message.replace(/{days}/g, days.toString());
      } else {
        message = message.replace(/{days}/g, days || "");
      }

      // Replace price placeholders
      message = message.replace(/{currentPrice}/g, currentPrice.toString());
      message = message.replace(
        /{discountedPrice}/g,
        discountedPrice.toString()
      );

      console.log("Final generated message:", message);
      return message;
    } catch (error) {
      console.error("Error generating message:", error);
      alert(`Error generating message: ${error.message}`);
      return "Error generating message. Please try again.";
    }
  };

  // State for tracking which message was just sent
  const [sentMessageId, setSentMessageId] = useState(null);

  // Send renewal message to client via appropriate platform
  // Send renewal message to client via appropriate platform
  // Force refresh of message template from Firestore
  // Helper function to process template with sale data
  const processTemplate = async (template, sale) => {
    try {
      // Use default template if no template provided
      const defaultTemplate = `السلام عليكم 
اهلا بحضرتك 
باقي على انتهاء رخصة {productName} 
يوم {days} 
استفد من خصم التجديد المبكر {discountedPrice}
بدلا من {currentPrice}
البريد الالكتروني: {clientEmail}`;

      // If we don't have a valid template, use default
      let templateToUse =
        template && template.trim() !== "" ? template : defaultTemplate;

      // Get the product price as fallback
      const product = products.find((p) => p.name === sale.productName);
      const productPrice = product ? product.price : sale.price || 0;

      // Initialize with product price in case we need to fall back
      let currentPrice = productPrice;
      let discountedPrice = Math.round(productPrice * 0.9); // 10% discount as fallback

      // IMPORTANT: Use the renewalPrices prop passed from parent component
      if (renewalPrices && renewalPrices[sale.productName]) {
        console.log(
          `✅ Using renewal prices from props for ${sale.productName}:`,
          renewalPrices[sale.productName]
        );

        // Use settings prices if available
        currentPrice =
          renewalPrices[sale.productName].currentPrice || currentPrice;
        discountedPrice =
          renewalPrices[sale.productName].discountedPrice || discountedPrice;
      } else {
        console.log(
          `⚠️ No renewal prices found for ${sale.productName}, using default price calculation`
        );
        console.log(
          `ℹ️ Using product price for ${sale.productName}: ${currentPrice} / ${discountedPrice}`
        );
      }

      const days = calculateExpirationDays(sale.date, sale.subscriptionPeriod);

      // Process the template by replacing all placeholders
      let message = templateToUse;

      // Replace product name
      message = message.replace(/{productName}/g, sale.productName || "");

      // Handle days which could be a special string
      if (typeof days === "number") {
        message = message.replace(/{days}/g, days.toString());
      } else {
        message = message.replace(/{days}/g, days || "");
      }

      // Replace price placeholders
      message = message.replace(/{currentPrice}/g, currentPrice.toString());
      message = message.replace(
        /{discountedPrice}/g,
        discountedPrice.toString()
      );

      // Replace client email
      message = message.replace(/{clientEmail}/g, sale.clientEmail || "");

      // Replace client name
      message = message.replace(/{clientName}/g, sale.clientName || "");

      console.log("Final generated message:", message);
      return message;
    } catch (error) {
      console.error("Error generating message:", error);
      alert(`Error generating message: ${error.message}`);
      return "Error generating message. Please try again.";
    }
  };

  // Direct fetch from REST API bypassing Firebase caching
  const fetchTemplateDirectly = async () => {
    try {
      const db = window.firebase ? window.firebase.firestore() : null;
      if (!db) {
        console.error("Firebase not initialized");
        return null;
      }

      const firebaseConfig = db._app.options;
      const projectId = firebaseConfig.projectId;
      const cacheBuster = Date.now();

      // Build direct URL to Firestore REST API
      const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/Settings/renewalMessage?key=${cacheBuster}`;

      console.log("Fetching template directly from:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      const data = await response.json();

      if (data && data.fields && data.fields.template) {
        const directTemplate = data.fields.template.stringValue;
        console.log("✅ Template fetched directly:", directTemplate);

        // Store in local state
        setRenewalMessageTemplate(directTemplate);
        setTemplateLastUpdated(Date.now());

        return directTemplate;
      } else {
        console.warn("No template found in direct API response");
        return null;
      }
    } catch (error) {
      console.error("Error in direct template fetch:", error);
      return null;
    }
  };

  const forceRefreshRenewalTemplate = async () => {
    try {
      console.log("🔄 Force refreshing renewal template from server...");
      setIsTemplateLoading(true);

      // Try the direct API approach first
      const directTemplate = await fetchTemplateDirectly();
      if (directTemplate) {
        setIsTemplateLoading(false);
        return directTemplate;
      }

      // Fall back to Firebase SDK methods if direct fetch fails
      const db = window.firebase ? window.firebase.firestore() : null;
      if (!db) {
        console.error("Firebase not initialized");
        setIsTemplateLoading(false);
        return null;
      }

      // Generate a unique cache-busting URL parameter
      const cacheBuster = Date.now();

      // Use a reference with a cache-busting query parameter
      const messageRef = db.collection("Settings").doc(`renewalMessage`);

      // Clear any local cache in the JavaScript client
      db.clearPersistence().catch((err) =>
        console.log("Persistence clearing not supported or already cleared")
      );

      // Force server fetch using an option that bypasses cache
      const messageDoc = await messageRef.get({
        source: "server",
        cacheBusting: cacheBuster, // Add a random parameter to bypass cache
      });

      if (
        messageDoc.exists &&
        messageDoc.data() &&
        messageDoc.data().template
      ) {
        const template = messageDoc.data().template;
        console.log("✅ Fresh template fetched successfully:", template);
        console.log("Template length:", template.length);

        // Update the local state immediately
        setRenewalMessageTemplate(template);
        setTemplateLastUpdated(cacheBuster);

        setIsTemplateLoading(false);
        return template;
      } else {
        console.warn("⚠️ No template found during forced refresh!");
        setIsTemplateLoading(false);
        return null;
      }
    } catch (error) {
      console.error("🔄 Critical error during template refresh:", error);
      setIsTemplateLoading(false);
      return null;
    }
  };

  // Copy renewal message to clipboard
  const copyRenewalMessage = async (sale) => {
    try {
      setCopiedId(null); // Reset copied status

      console.log("🚀 Preparing to copy message for:", sale.clientName);

      // CRITICAL CHANGE: Fetch the template first and wait for it
      const freshTemplate = await getTemplate();
      console.log("Fresh template for copying:", freshTemplate);

      if (!freshTemplate) {
        alert(
          "Could not fetch template from server. Message will use default template."
        );
      }

      // Use the freshly fetched template directly
      let message = await processTemplate(freshTemplate, sale);

      if (!message || message.trim() === "") {
        console.error("Cannot copy empty message");
        alert(
          "Error: Generated message is empty. Please check the template in Settings."
        );
        return;
      }

      // Use the clipboard API with proper error handling
      try {
        await navigator.clipboard.writeText(message);
        console.log("Message copied successfully!");
        setCopiedId(sale.transactionId);
        setTimeout(() => setCopiedId(null), 2000); // Reset after 2 seconds
      } catch (clipboardError) {
        console.error("Clipboard API error:", clipboardError);

        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = message;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            console.log("Fallback copy successful");
            setCopiedId(sale.transactionId);
            setTimeout(() => setCopiedId(null), 2000);
          } else {
            throw new Error("Fallback copy failed");
          }
        } catch (fallbackError) {
          console.error("Fallback copy error:", fallbackError);
          alert("Failed to copy message. Please try again or copy manually.");
        }

        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error("Error in copyRenewalMessage:", error);
      alert(`Error preparing message: ${error.message}`);
    }
  };

  const sendRenewalMessage = async (sale) => {
    try {
      setSentMessageId(null); // Reset sent status

      console.log("🚀 Preparing to send message for:", sale.clientName);

      // CRITICAL CHANGE: Fetch the template first and wait for it
      const freshTemplate = await getTemplate();
      console.log("Fresh template for sending:", freshTemplate);

      if (!freshTemplate) {
        alert(
          "Could not fetch template from server. Message will use default template."
        );
      }

      // Use the freshly fetched template directly
      let message = await processTemplate(freshTemplate, sale);

      if (!message || message.trim() === "") {
        console.error("Cannot send empty message");
        alert(
          "Error: Generated message is empty. Please check the template in Settings."
        );
        return;
      }

      // Check for contact info
      const contact1 = sale.contact1;
      const contact1Type = sale.contact1Type || "Whatsapp";
      const contact2 = sale.contact2;
      const contact2Type = sale.contact2Type || "Whatsapp";

      // For demo purposes, prefer contact1 if available
      let contactToUse = contact1;
      let contactTypeToUse = contact1Type;

      if (!contactToUse && contact2) {
        contactToUse = contact2;
        contactTypeToUse = contact2Type;
      }

      if (!contactToUse) {
        alert("No contact information found for this client.");
        return;
      }

      const encodedMessage = encodeURIComponent(message);
      let url = "";

      // Format phone numbers - remove non-numeric characters
      const formatPhone = (phone) => {
        // Remove any non-numeric characters except '+' at the beginning
        const cleaned = phone
          .replace(/^(\+)/, "PLUSSIGN")
          .replace(/\D/g, "")
          .replace("PLUSSIGN", "+");

        // If no country code, assume Egypt (+20)
        if (!cleaned.startsWith("+")) {
          if (cleaned.startsWith("0")) {
            return "+2" + cleaned;
          } else if (cleaned.startsWith("2")) {
            return "+" + cleaned;
          } else {
            return "+20" + cleaned;
          }
        }

        return cleaned;
      };

      // Create URL based on platform
      switch (contactTypeToUse) {
        case "Whatsapp":
          // Format phone for WhatsApp
          const phoneForWhatsApp = formatPhone(contactToUse).replace("+", "");
          url = `whatsapp://send?phone=${phoneForWhatsApp}&text=${encodedMessage}`;
          break;

        case "Telegram":
          try {
            // Copy message to clipboard first as this is the most reliable method
            navigator.clipboard.writeText(message);

            // Set the sent status for UI feedback
            setSentMessageId(sale.transactionId);

            // Show different instructions based on contact type
            const isPhoneNumber = /^\+?\d+$/.test(contactToUse);
            const hasUsername =
              contactToUse.startsWith("@") ||
              (!isPhoneNumber && !contactToUse.includes(" "));

            // For contacts with only names (no username/phone), just use clipboard
            if (!isPhoneNumber && !hasUsername) {
              alert(
                "Message copied for Telegram contact: " +
                  sale.clientName +
                  "\n\n" +
                  "IMPORTANT: This contact only has a display name without username or phone number.\n" +
                  "Telegram cannot directly open contacts by name only.\n\n" +
                  "Please:\n" +
                  "1. Open Telegram manually\n" +
                  "2. Find the contact: " +
                  contactToUse +
                  "\n" +
                  "3. Paste the message (Ctrl+V)"
              );
              return;
            }

            // Format the contact info for Telegram protocol
            let telegramContact = contactToUse;
            if (isPhoneNumber) {
              // If it's a phone number, format it (remove +)
              telegramContact = formatPhone(telegramContact).replace("+", "");
              // Try to launch Telegram Desktop with this phone
              url = `tg://resolve?phone=${telegramContact}`;
            } else {
              // If it's a username, ensure it doesn't have @ prefix
              telegramContact = telegramContact.replace(/^@/, "");
              // Try to launch with username
              url = `tg://resolve?domain=${telegramContact}`;
            }

            // Try multiple methods to launch Telegram
            const telegramWindow = window.open(url, "_blank");

            // Telegram app might not respond to the protocol, so check if window opened
            if (
              !telegramWindow ||
              telegramWindow.closed ||
              typeof telegramWindow.closed === "undefined"
            ) {
              // Try alternative URL format if first attempt failed
              setTimeout(() => {
                if (isPhoneNumber) {
                  window.open(`https://t.me/${telegramContact}`, "_blank");
                } else {
                  window.open(`https://t.me/${telegramContact}`, "_blank");
                }
              }, 300);
            }

            // Show instructions with a short delay to ensure the message appears after the app launch attempt
            setTimeout(() => {
              alert(
                "Attempting to open Telegram with " +
                  (isPhoneNumber ? "phone number" : "username") +
                  "...\n\n" +
                  "IMPORTANT: The message has already been copied to your clipboard!\n\n" +
                  "If Telegram opens:\n" +
                  "1. Find the conversation that opens\n" +
                  "2. Click in the message field\n" +
                  "3. Paste the message (Ctrl+V)\n\n" +
                  "If Telegram doesn't open:\n" +
                  "1. Open Telegram manually\n" +
                  "2. Find contact: " +
                  contactToUse +
                  "\n" +
                  "3. Paste the message"
              );
            }, 1000);

            return;
          } catch (err) {
            console.error("Failed with Telegram:", err);
            alert(
              "The message has been copied to your clipboard.\n\n" +
                "Please:\n" +
                "1. Open Telegram manually\n" +
                "2. Find contact: " +
                contactToUse +
                "\n" +
                "3. Paste the message (Ctrl+V)"
            );
            return;
          }

        case "Messenger":
          // Always copy the message to clipboard first as the most reliable method
          try {
            navigator.clipboard.writeText(message);

            // Set sent status for UI feedback
            setSentMessageId(sale.transactionId);
            setTimeout(() => setSentMessageId(null), 2000);

            // Create a combined clipboard content with client name for easy searching
            const clipboardSearchHelper = `Message for: ${sale.clientName}\n\n${message}`;
            navigator.clipboard.writeText(clipboardSearchHelper);

            // Extract messenger-specific information for deep linking
            let fbUserId = "";
            let fbUsername = "";
            let useBusinessUrl = false;
            let finalUrls = [];

            // Check if we should use the global Facebook Business URL
            if (facebookBusinessUrl && facebookBusinessUrl.trim() !== "") {
              useBusinessUrl = true;
              finalUrls.push(facebookBusinessUrl);
            }

            // Extract user ID or username from contact information
            if (contactToUse.includes("facebook.com")) {
              // Profile URL format: facebook.com/profile.php?id=12345
              const idMatch = contactToUse.match(/profile\.php\?id=(\d+)/);
              if (idMatch && idMatch[1]) {
                fbUserId = idMatch[1];
                finalUrls.unshift(
                  `https://www.facebook.com/messages/t/${fbUserId}`
                );
                finalUrls.unshift(`https://m.me/${fbUserId}`);
                finalUrls.unshift(`https://www.messenger.com/t/${fbUserId}`);
              }
              // Username format: facebook.com/username
              else {
                const usernameMatch = contactToUse.match(
                /facebook\.com\/([^/?]+)/
                );
                if (usernameMatch && usernameMatch[1]) {
                  fbUsername = usernameMatch[1];
                  finalUrls.unshift(
                    `https://www.facebook.com/messages/t/${fbUsername}`
                  );
                  finalUrls.unshift(`https://m.me/${fbUsername}`);
                  finalUrls.unshift(
                    `https://www.messenger.com/t/${fbUsername}`
                  );
                } else {
                  // Can't parse the URL, just use it directly
                  finalUrls.push(contactToUse);
                }
              }
            }
            // Direct username (no facebook.com)
            else if (
              !contactToUse.includes(".") &&
              !contactToUse.includes(" ")
            ) {
              fbUsername = contactToUse;
              finalUrls.unshift(
                `https://www.facebook.com/messages/t/${fbUsername}`
              );
              finalUrls.unshift(`https://m.me/${fbUsername}`);
              finalUrls.unshift(`https://www.messenger.com/t/${fbUsername}`);
            }
            // Last resort: just use whatever was provided
            else if (!useBusinessUrl) {
              finalUrls.push(
                `https://www.facebook.com/messages/t/${encodeURIComponent(
                  contactToUse
                )}`
              );
              finalUrls.push(`https://www.messenger.com`); // At least open Messenger
            }

            // Add general Messenger URLs as last resort
            if (!useBusinessUrl) {
              finalUrls.push("https://www.messenger.com");
              finalUrls.push("https://www.facebook.com/messages/");
            }

            // Display instructions with specific details based on our linking strategy
            const instructions = useBusinessUrl
              ? `Message copied to clipboard for Facebook Business.\n\n` +
                `IMPORTANT: When Facebook Business loads:\n` +
                `1. You may see a gray overlay - click "Close" in the top-left corner\n` +
                `2. Search for "${sale.clientName}" in the inbox\n` +
                `3. Open their conversation and paste the message (Ctrl+V)`
              : fbUserId || fbUsername
              ? `Message copied to clipboard.\n\n` +
                `Attempting to open direct conversation with ${sale.clientName}...\n\n` +
                `If a direct conversation doesn't open:\n` +
                `1. Search for "${sale.clientName}" in Messenger\n` +
                `2. Paste the message when you find them (Ctrl+V)`
              : `Message copied to clipboard.\n\n` +
                `We couldn't find a direct link to ${sale.clientName}.\n` +
                `Messenger will open, then please:\n` +
                `1. Search for "${sale.clientName}"\n` +
                `2. Paste the message (Ctrl+V)`;

            alert(instructions);

            // Try multiple URLs with sequential timeouts to maximize chances of success
            if (finalUrls.length > 0) {
              // Try the first URL immediately
              console.log(
                `Trying Facebook URL (1/${finalUrls.length}): ${finalUrls[0]}`
              );
              window.open(finalUrls[0], "_blank");

              // Try additional URLs with short delays between attempts
              finalUrls.slice(1).forEach((url, index) => {
                setTimeout(() => {
                  console.log(
                    `Trying Facebook URL (${index + 2}/${
                      finalUrls.length
                    }): ${url}`
                  );
                  window.open(url, "_blank");
                }, (index + 1) * 800); // Increase delay for each attempt
              });
            }

            return; // Skip the standard URL opening code
          } catch (err) {
            console.error("Failed to handle Facebook Messenger:", err);
            alert(
              `Error connecting to Facebook Messenger. The message has been copied to your clipboard.\n\n` +
                `Please open Facebook Messenger manually, find ${sale.clientName}, and paste the message.`
            );
            return;
          }

        default:
          alert(`Unsupported contact type: ${contactTypeToUse}`);
          return;
      }

      // Try to open the URL for other platforms (mainly WhatsApp)
      try {
        // Track which message was sent
        setSentMessageId(sale.transactionId);
        setTimeout(() => setSentMessageId(null), 2000); // Reset after 2 seconds

        // Open the URL
        window.open(url, "_blank");
        console.log(`Sent message via ${contactTypeToUse} to ${contactToUse}`);
      } catch (error) {
        console.error(`Error sending message via ${contactTypeToUse}:`, error);
        alert(
          `Failed to send message via ${contactTypeToUse}. Please try copying and pasting manually.`
        );
      }
    } catch (error) {
      console.error("Error in sendRenewalMessage:", error);
      alert(`Error preparing message: ${error.message}`);
    }
  };

  // Bulk Edit Functions
  const handleSelectRow = (transactionId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) {
        newSet.delete(transactionId);
      } else {
        newSet.add(transactionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = filteredData.map(row => row.transactionId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleBulkUpdateInternal = async (updates) => {
    setIsBulkUpdating(true);
    try {
      const transactionIds = Array.from(selectedRows);
      
      // Use the parent's bulk update handler if available, otherwise fall back to refreshData
      if (typeof handleBulkUpdate === 'function') {
        const success = await handleBulkUpdate(updates, transactionIds);
        if (success) {
          // Clear selection and close modal
          setSelectedRows(new Set());
          setShowBulkEditModal(false);
        }
      } else {
        // Fallback to old method
        const updatePromises = transactionIds.map(async (transactionId) => {
          const transactionRef = doc(db, 'Sales', transactionId);
          return updateDoc(transactionRef, {
            ...updates,
            updatedAt: new Date()
          });
        });

        await Promise.all(updatePromises);
        
        // Clear selection and close modal
        setSelectedRows(new Set());
        setShowBulkEditModal(false);
        
        // Refresh data by calling a refresh function
        if (typeof refreshData === 'function') {
          await refreshData();
        }
        
        alert(`Successfully updated ${selectedRows.size} records!`);
      }
    } catch (error) {
      console.error('Bulk update error:', error);
      alert(`Failed to update records: ${error.message}`);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // Filter the data based on showNoRenewalRecords and showRenewedRecords
  const filteredData = data.filter((row) => {
    if (!showNoRenewalRecords && noRenewalTransactions.has(row.transactionId)) {
      return false;
    }
    if (!showRenewedRecords && (renewedTransactions.has(row.transactionId) || row.renewedTo)) {
      return false;
    }
    return true;
  });

  // Get styling for a row based on expiration days
  const getRowStyle = (row) => {
    if (row.isGroupedRow) return {}; // No special styling for grouped rows

    // Calculate days until expiration
    const days = calculateExpirationDays(row.date, row.subscriptionPeriod);

    // One-time purchases or invalid dates don't need expiration highlighting
    if (
      days === "∞" ||
      days === "Invalid Date" ||
      days === "Invalid Period" ||
      days === "N/A"
    ) {
      return {};
    }

    // Expired (negative days)
    if (days < 0) {
      return {
        backgroundColor: "rgba(254, 202, 202, 0.7)", // More visible red
        borderLeft: "4px solid #dc2626", // Red border for emphasis
      };
    }

    // Expiring very soon (within 7 days)
    if (days <= 7) {
      return {
        backgroundColor: "rgba(254, 215, 170, 0.7)", // Orange background
        borderLeft: "4px solid #ea580c", // Orange border
      };
    }

    // Expiring soon (within 30 days)
    if (days <= 30) {
      return {
        backgroundColor: "rgba(254, 240, 138, 0.7)", // More visible yellow
        borderLeft: "4px solid #d97706", // Amber border
      };
    }

    return {};
  };

  // Format expiration days for display - simplified to just show the number
  const formatExpirationDays = (days) => {
    // Handle special cases
    if (
      days === "∞" ||
      days === "Invalid Date" ||
      days === "Invalid Period" ||
      days === "N/A"
    ) {
      return days;
    }

    // Create badge-style display with appropriate colors
    let badgeStyle = {};
    let text = "";

    // Expired
    if (days < 0) {
      badgeStyle = {
        backgroundColor: "#fee2e2", // Light red background
        color: "#dc2626", // Red text
        border: "1px solid #fecaca", // Red border
        padding: "2px 6px",
        borderRadius: "4px",
        fontWeight: "700",
        display: "inline-block", // Make it a block element
      };
      text = days; // Just show the negative number
    }
    // Very soon (within 7 days)
    else if (days <= 7) {
      badgeStyle = {
        backgroundColor: "#ffedd5", // Light orange background
        color: "#ea580c", // Orange text
        border: "1px solid #fed7aa", // Orange border
        padding: "2px 6px",
        borderRadius: "4px",
        fontWeight: "700",
        display: "inline-block",
      };
      text = days; // Just show the number
    }
    // Soon (within 30 days)
    else if (days <= 30) {
      badgeStyle = {
        backgroundColor: "#fef9c3", // Light yellow background
        color: "#d97706", // Amber text
        border: "1px solid #fef08a", // Yellow border
        padding: "2px 6px",
        borderRadius: "4px",
        fontWeight: "700",
        display: "inline-block",
      };
      text = days; // Just show the number
    }
    // Valid but not soon
    else {
      badgeStyle = {
        backgroundColor: "#dcfce7", // Light green background
        color: "#16a34a", // Green text
        border: "1px solid #bbf7d0", // Green border
        padding: "2px 6px",
        borderRadius: "4px",
        fontWeight: "700",
        display: "inline-block",
      };
      text = days; // Just show the number
    }

    return <span style={badgeStyle}>{text}</span>;
  };

  // Get status color and badge styling
  const getStatusBadge = (status) => {
    let badgeStyle = {
      padding: "2px 6px",
      borderRadius: "4px",
      fontWeight: "500",
      display: "inline-block",
    };

    switch (status) {
      case "Completed":
        badgeStyle = {
          ...badgeStyle,
          backgroundColor: "#dcfce7", // Light green background
          color: "#16a34a", // Green text
          border: "1px solid #bbf7d0", // Green border
        };
        break;
      case "OnHold":
        badgeStyle = {
          ...badgeStyle,
          backgroundColor: "#fef9c3", // Light yellow background
          color: "#d97706", // Amber text
          border: "1px solid #fef08a", // Yellow border
        };
        break;
      case "Processing":
        badgeStyle = {
          ...badgeStyle,
          backgroundColor: "#dbeafe", // Light blue background
          color: "#2563eb", // Blue text
          border: "1px solid #bfdbfe", // Blue border
        };
        break;
      case "Refunded":
        badgeStyle = {
          ...badgeStyle,
          backgroundColor: "#fee2e2", // Light red background
          color: "#dc2626", // Red text
          border: "1px solid #fecaca", // Red border
        };
        break;
      case "Expired":
        badgeStyle = {
          ...badgeStyle,
          backgroundColor: "#fee2e2", // Light red background
          color: "#dc2626", // Red text
          border: "1px solid #fecaca", // Red border
        };
        break;
      default:
        badgeStyle = {
          ...badgeStyle,
          backgroundColor: "#f3f4f6", // Light gray background
          color: "#6b7280", // Gray text
          border: "1px solid #e5e7eb", // Gray border
        };
    }

    return <span style={badgeStyle}>{status}</span>;
  };

  // Styles for action buttons
  const styles = {
    actionButtons: {
      display: "flex",
      gap: "6px",
      alignItems: "center",
      minWidth: "240px",
      justifyContent: "flex-start",
    },
    actionButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "4px",
      padding: "6px 12px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
      border: "none",
      height: "28px",
      width: "auto",
      minWidth: "60px",
      whiteSpace: "nowrap",
      transition: "background-color 0.2s ease",
    },
    editButton: {
      backgroundColor: "#ffd600",
      color: "#1e293b",
    },
    deleteButton: {
      backgroundColor: "#ef4444",
      color: "white",
    },
    duplicateButton: {
      backgroundColor: "#8B5CF6",
      color: "white",
      "&:hover": {
        backgroundColor: "#7C3AED",
      },
    },
    renewButton: {
      backgroundColor: "#10B981",
      color: "white",
      "&:hover": {
        backgroundColor: "#059669",
      },
    },
    copyButton: {
      backgroundColor: "#0ea5e9",
      color: "white",
      "&:hover": {
        backgroundColor: "#0284c7",
      },
    },
    copiedButton: {
      backgroundColor: "#14b8a6",
      color: "white",
    },
    expiredBadge: {
      backgroundColor: "#fee2e2",
      color: "#dc2626",
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "500",
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      marginLeft: "8px",
    },
    noteButton: {
      backgroundColor: "#e0f2fe",
      color: "#0284c7",
      border: "1px solid #bae6fd",
      padding: "3px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
    },
  };

  // Quick edit form component that appears when a row is expanded - modified to handle colspan
  const QuickEditForm = ({ transactionId, onSave, onCancel, colspan }) => {
    console.log("=== QuickEditForm Debug ===");
    console.log("1. QuickEditForm rendered for transactionId:", transactionId);
    console.log("2. QuickEditForm props:", {
      transactionId,
      onSave,
      onCancel,
      colspan,
    });
    console.log("3. Current quickEditData:", quickEditData);
    console.log("4. Row data for this form:", quickEditData[transactionId]);

    const rowData = quickEditData[transactionId];
    console.log("5. QuickEditForm rowData:", rowData);

    // We must declare hooks at the top level, not conditionally
    const [notesValue, setNotesValue] = useState("");
    const notesRef = useRef(null);
    const [isUnknownDate, setIsUnknownDate] = useState(false);

    // Effect to sync notes value with rowData and set isUnknownDate
    useEffect(() => {
      console.log("QuickEditForm useEffect triggered");
      if (rowData) {
        console.log("Setting notes value:", rowData.notes);
        setNotesValue(rowData.notes || "");
        setIsUnknownDate(rowData.date === "unknown");
      } else {
        console.error("No rowData available in useEffect");
      }
    }, [rowData]);

    // If no rowData, don't render anything but hooks have already been called
    if (!rowData) {
      console.log("QuickEditForm: No rowData available, not rendering");
      return null;
    }

    console.log("QuickEditForm: Rendering form with data");

    // Handle notes change with focus retention
    const handleNotesChange = (e) => {
      const value = e.target.value;
      setNotesValue(value);
      handleQuickEditChange(transactionId, "notes", value);
    };

    // Add this new function to handle textarea focus
    const handleNotesFocus = () => {
      if (notesRef.current) {
        notesRef.current.focus();
      }
    };

    // Add this new function to handle textarea blur
    const handleNotesBlur = () => {
      // Only blur if we're not actively editing
      if (!notesRef.current?.contains(document.activeElement)) {
        notesRef.current?.blur();
      }
    };

    // Handle unknown date toggle
    const handleUnknownDateChange = (e) => {
      const checked = e.target.checked;
      setIsUnknownDate(checked);

      if (checked) {
        // Save current date temporarily and set to unknown
        handleQuickEditChange(transactionId, "_previousDate", rowData.date);
        handleQuickEditChange(transactionId, "date", "unknown");
      } else {
        // Restore previous date or use today
        const previousDate =
          rowData._previousDate || new Date().toISOString().split("T")[0];
        handleQuickEditChange(transactionId, "date", previousDate);
      }
    };

    // Find product item from products array
    const findProduct = (productName) => {
      return products.find((p) => p.name === productName) || {};
    };

    // Handle product selection and auto-fill price
    const handleProductChange = (e) => {
      const productName = e.target.value;
      handleQuickEditChange(transactionId, "productName", productName);

      // Auto-fill price if available
      const product = findProduct(productName);
      if (product && product.price) {
        handleQuickEditChange(transactionId, "price", product.price);
      }
    };

    // Define common styling for better maintainability
    const styles = {
      fieldGroup: {
        marginBottom: "16px",
      },
      sectionTitle: {
        fontSize: "14px",
        fontWeight: "600",
        marginBottom: "8px",
        paddingBottom: "4px",
        borderBottom: "1px solid #e5e7eb",
        color: "#374151",
      },
      label: {
        display: "block",
        fontSize: "14px",
        fontWeight: "500",
        marginBottom: "4px",
        color: "#4b5563",
      },
      input: {
        width: "100%",
        padding: "8px 12px",
        border: "1px solid #d1d5db",
        borderRadius: "4px",
        fontSize: "14px",
      },
      select: {
        width: "100%",
        padding: "8px 12px",
        border: "1px solid #d1d5db",
        borderRadius: "4px",
        fontSize: "14px",
        backgroundColor: "white",
      },
    };

    return (
      <td colSpan={colspan} style={{ padding: 0 }}>
          <div
            style={{
              padding: "16px",
              backgroundColor: "#f9fafb",
              borderTop: "1px dashed #e5e7eb",
              borderBottom: "1px dashed #e5e7eb",
              transition: "all 0.3s ease",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "20px",
                color: "#4b5563",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Quick Edit</span>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  backgroundColor: "#e5e7eb",
                  padding: "2px 6px",
                  borderRadius: "4px",
                }}
              >
                Transaction ID: {transactionId}
              </div>
            </h3>

            {/* Main form grid with 3 columns */}
            <div style={{ marginBottom: "20px" }}>
              {/* Client Information Section */}
              <div style={styles.fieldGroup}>
                <h4 style={styles.sectionTitle}>Client Information</h4>
                <div
                  style={{
                    display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {/* Client Name */}
                  <div>
                    <label style={styles.label}>Client Name</label>
                    <input
                      type="text"
                      value={rowData.clientName || ""}
                      onChange={(e) =>
                        handleQuickEditChange(
                          transactionId,
                          "clientName",
                          e.target.value
                        )
                      }
                      style={styles.input}
                    />
                  </div>

                  {/* Client Email */}
                  <div>
                    <label style={styles.label}>Client Email</label>
                    <input
                      type="email"
                      value={rowData.clientEmail || ""}
                      onChange={(e) =>
                        handleQuickEditChange(
                          transactionId,
                          "clientEmail",
                          e.target.value
                        )
                      }
                      style={styles.input}
                    />
                  </div>

                  {/* Edu Email */}
                  <div>
                    <label style={styles.label}>Edu Email</label>
                    <input
                      type="email"
                      value={rowData.emailEdu || ""}
                      onChange={(e) =>
                        handleQuickEditChange(
                          transactionId,
                          "emailEdu",
                          e.target.value
                        )
                      }
                      style={styles.input}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div style={styles.fieldGroup}>
                <h4 style={styles.sectionTitle}>Contact Information</h4>
                <div
                  style={{
                    display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {/* Contact 1 with type */}
                  <div style={{ display: "flex", gap: "8px" }}>
                    <div style={{ flex: "3" }}>
                      <label style={styles.label}>Contact 1</label>
                      <input
                        type="text"
                        value={rowData.contact1 || ""}
                        onChange={(e) =>
                          handleQuickEditChange(
                            transactionId,
                            "contact1",
                            e.target.value
                          )
                        }
                        style={styles.input}
                      />
                    </div>
                    <div style={{ flex: "2" }}>
                      <label style={styles.label}>Type</label>
                      <select
                        value={rowData.contact1Type || "Whatsapp"}
                        onChange={(e) =>
                          handleQuickEditChange(
                            transactionId,
                            "contact1Type",
                            e.target.value
                          )
                        }
                        style={styles.select}
                      >
                        <option value="Whatsapp">Whatsapp</option>
                        <option value="Telegram">Telegram</option>
                        <option value="Messenger">Messenger</option>
                        <option value="Email">Email</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Contact 2 with type */}
                  <div style={{ display: "flex", gap: "8px" }}>
                    <div style={{ flex: "3" }}>
                      <label style={styles.label}>Contact 2</label>
                      <input
                        type="text"
                        value={rowData.contact2 || ""}
                        onChange={(e) =>
                          handleQuickEditChange(
                            transactionId,
                            "contact2",
                            e.target.value
                          )
                        }
                        style={styles.input}
                      />
                    </div>
                    <div style={{ flex: "2" }}>
                      <label style={styles.label}>Type</label>
                      <select
                        value={rowData.contact2Type || "Whatsapp"}
                        onChange={(e) =>
                          handleQuickEditChange(
                            transactionId,
                            "contact2Type",
                            e.target.value
                          )
                        }
                        style={styles.select}
                      >
                        <option value="Whatsapp">Whatsapp</option>
                        <option value="Telegram">Telegram</option>
                        <option value="Messenger">Messenger</option>
                        <option value="Email">Email</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product and Payment Section */}
              <div style={styles.fieldGroup}>
                <h4 style={styles.sectionTitle}>Product & Payment Details</h4>
                <div
                  style={{
                    display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {/* Product Selection */}
                  <div>
                    <label style={styles.label}>Product</label>
                    <select
                      value={rowData.productName || ""}
                      onChange={handleProductChange}
                      style={styles.select}
                    >
                      <option value="">Select a product</option>
                      {products.map((product) => (
                        <option
                          key={product.id || product.name}
                          value={product.name}
                        >
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Product Variant for Autodesk */}
                  {rowData.productName === "Autodesk" && (
                    <div>
                      <label style={styles.label}>Autodesk Variant</label>
                      <input
                        type="text"
                        value={rowData.productVariant || ""}
                        onChange={(e) =>
                          handleQuickEditChange(
                            transactionId,
                            "productVariant",
                            e.target.value
                          )
                        }
                        style={styles.input}
                        placeholder="Enter variant"
                      />
                    </div>
                  )}

                  {/* Price */}
                  <div>
                    <label style={styles.label}>Price (EGP)</label>
                    <input
                      type="number"
                      value={rowData.price || ""}
                      onChange={(e) =>
                        handleQuickEditChange(
                          transactionId,
                          "price",
                          e.target.value
                        )
                      }
                      style={styles.input}
                    />
                  </div>

                  {/* Amount */}
                  <div>
                    <label style={styles.label}>Amount</label>
                    <input
                      type="number"
                      value={rowData.amount || ""}
                      onChange={(e) =>
                        handleQuickEditChange(
                          transactionId,
                          "amount",
                          e.target.value
                        )
                      }
                      style={styles.input}
                    />
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label style={styles.label}>Payment Method</label>
                    <select
                      value={rowData.paymentMethod || "instaPay"}
                      onChange={(e) =>
                        handleQuickEditChange(
                          transactionId,
                          "paymentMethod",
                          e.target.value
                        )
                      }
                      style={styles.select}
                    >
                      <option value="instaPay">InstaPay</option>
                      <option value="Vfcash">Vfcash</option>
                      <option value="Binance">Binance</option>
                      <option value="Gate.io">Gate.io</option>
                      <option value="Paypal">Paypal</option>
                      <option value="Cash">Cash</option>
                      <option value="N/A">N/A</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Subscription Details */}
              <div style={styles.fieldGroup}>
                <h4 style={styles.sectionTitle}>Subscription Details</h4>
                <div
                  style={{
                    display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {/* Date with Unknown Date Toggle */}
                  <div>
                    <label style={styles.label}>Date</label>
                    <div style={{ marginBottom: "8px" }}>
                      <input
                        type="date"
                        value={isUnknownDate ? "" : rowData.date || ""}
                        onChange={(e) =>
                          handleQuickEditChange(
                            transactionId,
                            "date",
                            e.target.value
                          )
                        }
                        style={styles.input}
                        disabled={isUnknownDate}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#ffedd5",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        border: "1px solid #fed7aa",
                      }}
                    >
                      <input
                        type="checkbox"
                        id={`unknownDate-${transactionId}`}
                        checked={isUnknownDate}
                        onChange={handleUnknownDateChange}
                        style={{ marginRight: "8px" }}
                      />
                      <label
                        htmlFor={`unknownDate-${transactionId}`}
                        style={{ fontSize: "12px", color: "#9a3412" }}
                      >
                        Unknown Date
                      </label>
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label style={styles.label}>Status</label>
                    <select
                      value={rowData.status || "OnHold"}
                      onChange={(e) =>
                        handleQuickEditChange(
                          transactionId,
                          "status",
                          e.target.value
                        )
                      }
                      style={styles.select}
                    >
                      <option value="OnHold">OnHold</option>
                      <option value="Processing">Processing</option>
                      <option value="Completed">Completed</option>
                      <option value="Refunded">Refunded</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>

                  {/* Subscription Period */}
                  <div>
                    <label style={styles.label}>Subscription Period</label>
                    <select
                      value={rowData.subscriptionPeriod || "1 month"}
                      onChange={(e) =>
                        handleQuickEditChange(
                          transactionId,
                          "subscriptionPeriod",
                          e.target.value
                        )
                      }
                      style={styles.select}
                    >
                      <option value="1 month">1 month</option>
                      <option value="3 months">3 months</option>
                      <option value="6 months">6 months</option>
                      <option value="12 months">12 months</option>
                      <option value="24 months">24 months</option>
                      <option value="36 months">36 months</option>
                      <option value="one-time">One-time purchase</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Notes - Full Width */}
              <div style={styles.fieldGroup}>
                <h4 style={styles.sectionTitle}>Notes</h4>
                <textarea
                  ref={notesRef}
                  value={notesValue}
                  onChange={handleNotesChange}
                onFocus={handleNotesFocus}
                onBlur={handleNotesBlur}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                    minHeight: "80px",
                    resize: "vertical",
                  }}
                />
              </div>
            </div>

            {/* Buttons */}
            <div
              style={{
                display: "flex",
              justifyContent: "flex-start",
                gap: "8px",
                marginTop: "20px",
                borderTop: "1px solid #e5e7eb",
                paddingTop: "16px",
              }}
            >
              <button
              onClick={() => onSave(transactionId)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "8px 16px",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                  borderRadius: "4px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
              <Save size={16} />
              Save
              </button>
              <button
              onClick={() => onCancel(transactionId)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "8px 16px",
                backgroundColor: "#f3f4f6",
                color: "#4b5563",
                border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
              <X size={16} />
              Cancel
              </button>
            </div>
          </div>
        </td>
    );
  };

  // Check if user prefers dark mode
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Effect to check for dark mode preference
  useEffect(() => {
    // Check for OS dark mode preference
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    setIsDarkMode(prefersDark);

    // Listen for changes in preference
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Function to render the table rows with expandable quick edit forms
  // eslint-disable-next-line no-unused-vars
  const renderTableRows = (row) => {
    console.log("=== Row Render Debug ===");
    console.log("1. Rendering row:", row.transactionId);
    console.log("2. Is grouped row?", row.isGroupedRow);
    console.log("3. Current expandedRows state:", expandedRows);
    console.log("4. Is row expanded?", expandedRows[row.transactionId]);
    console.log("5. Current quickEditData state:", quickEditData);
    console.log("6. Row data:", row);

    const isExpanded = !row.isGroupedRow && expandedRows[row.transactionId];
    console.log("7. Final isExpanded value:", isExpanded);

    // Create an array to hold both the main row and the quick edit form if expanded
    const rows = [
      <tr key={`main-${row.transactionId}`} style={getRowStyle(row)}>
        {columns.map((column) => {
          if (!localVisibleColumns[column.id]) return null;
          return <td key={column.id}>{renderCellFunction(row, column.id)}</td>;
        })}
      </tr>,
    ];

    // Add quick edit form if row is expanded
    if (isExpanded) {
      console.log("8. Adding QuickEditForm for row:", row.transactionId);
      rows.push(
        <QuickEditForm
          key={`quick-edit-${row.transactionId}`}
          transactionId={row.transactionId}
          onSave={saveQuickEdit}
          onCancel={cancelQuickEdit}
        />
      );
    }

    return rows;
  };

  // Add a new column for the quick edit toggle if it doesn't exist
  const [updatedColumns, setUpdatedColumns] = useState(columns);

  // Add checkbox column for bulk selection
  useEffect(() => {
    const checkboxColumn = {
      id: 'select',
      label: 'Select', // Proper label for the select column
      accessor: 'select',
      width: 50,
      resizable: false,
      sortable: false
    };

    setUpdatedColumns(prev => {
      const hasCheckboxColumn = prev.some(col => col.id === 'select');
      if (!hasCheckboxColumn) {
        return [checkboxColumn, ...prev];
      }
      return prev;
    });
  }, [selectedRows, filteredData.length]);

  // Create a state for local visible columns management
  const [localVisibleColumns, setLocalVisibleColumns] =
    useState({...visibleColumns, select: true}); // Always include select column

  // Update localVisibleColumns to include select column for bulk edit
  useEffect(() => {
    setLocalVisibleColumns(prev => ({
      ...prev,
      select: true, // Always ensure select column is visible
    }));
  }, [visibleColumns]);

  // Update localVisibleColumns to include quick_edit
  useEffect(() => {
    if (!localVisibleColumns.hasOwnProperty("quick_edit")) {
      setLocalVisibleColumns((prev) => ({
        ...prev,
        quick_edit: true,
      }));
    }
  }, [localVisibleColumns]);

  // Add cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Clean up any expanded rows and quick edit data when component unmounts
      setExpandedRows({});
      setQuickEditData({});
      console.log("Cleaned up expanded rows and quick edit data on unmount");
    };
  }, []);

  // Update localVisibleColumns to remove quick_edit
  useEffect(() => {
    if (localVisibleColumns.hasOwnProperty("quick_edit")) {
      const newVisibleColumns = { ...localVisibleColumns };
      delete newVisibleColumns.quick_edit;
      setLocalVisibleColumns(newVisibleColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Track the previous expandedRows and filteredData to avoid unnecessary recalculations
  const prevExpandedRowsRef = useRef(expandedRows);
  const prevFilteredDataRef = useRef(filteredData);

  // Move expandedData generation to a useEffect with explicit dependency tracking
  const [expandedData, setExpandedData] = useState([]);

  useEffect(() => {
    console.log("Checking if expandedData needs to update");

    // Check if expandedRows changed
    const expandedRowsChanged =
      JSON.stringify(prevExpandedRowsRef.current) !==
      JSON.stringify(expandedRows);

    // Check if filteredData changed - only check IDs to avoid deep comparison
    const filteredDataChanged =
      prevFilteredDataRef.current.length !== filteredData.length ||
      JSON.stringify(
        prevFilteredDataRef.current.map((r) => r.transactionId)
      ) !== JSON.stringify(filteredData.map((r) => r.transactionId));

    // Only regenerate if something actually changed
    if (expandedRowsChanged || filteredDataChanged) {
      console.log("Regenerating expandedData due to changes");

      // Generate the expanded data
      const newExpandedData = [];
      filteredData.forEach((row) => {
        // Add the main row first
        newExpandedData.push({ ...row, _isMainRow: true });

        // If this row is expanded, create a special row for the quick edit form
        if (!row.isGroupedRow && expandedRows[row.transactionId]) {
          // Create a special row that will hold the quick edit form
          newExpandedData.push({
            transactionId: `quickedit-${row.transactionId}`,
            isQuickEditRow: true,
            originalTransactionId: row.transactionId,
            // Copy all other properties to ensure form has complete data
            ...row,
          });
        }
      });

      // Update the state and refs
      setExpandedData(newExpandedData);
      prevExpandedRowsRef.current = expandedRows;
      prevFilteredDataRef.current = filteredData;
    }
  }, [expandedRows, filteredData]); // These are still dependencies, but we'll control the updates

  // Get styling for a row based on expiration days and expanded status
  const getRowStyleWithExpansion = useMemo(() => {
    // Create the function inside the useMemo to avoid recreating it unnecessarily
    return (row) => {
      // If this is a quick edit row, make it transparent
      if (row.isQuickEditRow) {
        return {
          backgroundColor: "transparent",
          border: "none",
        };
      }

      // Get the base style for expiration days
      const baseStyle = getRowStyle(row);
      
      // Check if this row is selected
      const isSelected = selectedRows.has(row.transactionId);
      
      // If selected, add selection highlighting
      if (isSelected) {
        // Create a blue overlay effect for selected rows
        const selectedStyle = {
          ...baseStyle,
          backgroundColor: baseStyle.backgroundColor 
            ? "#e0f2fe" // Light blue-cyan for selected rows with existing background
            : "#dbeafe", // Light blue for selected rows
          borderLeft: "4px solid #0ea5e9", // Cyan-blue left border
          boxShadow: "inset 0 0 0 1px rgba(14, 165, 233, 0.3)", // Blue inner shadow
          position: "relative",
        };

        // If there was an existing background color (for expiration), blend it
        if (baseStyle.backgroundColor) {
          if (baseStyle.backgroundColor.includes("254, 202, 202")) {
            // Red expired + selected = pink-red
            selectedStyle.backgroundColor = "#fce7f3"; // Pink
            selectedStyle.borderLeft = "4px solid #ec4899"; // Pink border
          } else if (baseStyle.backgroundColor.includes("254, 215, 170")) {
            // Orange expiring + selected = orange-blue
            selectedStyle.backgroundColor = "#e0f2fe"; // Light cyan
            selectedStyle.borderLeft = "4px solid #0ea5e9"; // Cyan border
          } else if (baseStyle.backgroundColor.includes("254, 240, 138")) {
            // Yellow expiring + selected = yellow-blue
            selectedStyle.backgroundColor = "#e0f2fe"; // Light cyan
            selectedStyle.borderLeft = "4px solid #0ea5e9"; // Cyan border
          }
        }

        return selectedStyle;
      }

      return baseStyle;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]); // Add selectedRows as dependency so it updates when selection changes

  // Use refs to store current function values to avoid stale closures
  const handlersRef = useRef({});
  
  // Update refs with current function values during render, so cells
  // rendered in this pass read the latest state (not the previous render's)
  handlersRef.current = {
      saveQuickEdit,
      cancelQuickEdit,
      handleEditButtonClick,
      handleDelete,
      handleDuplicate,
      handleRenew,
      copyRenewalMessage,
      sendRenewalMessage,
      openNoteModal,
      onRenewedToggle,
      onNoRenewalToggle,
      calculateExpirationDays,
      // State values
      renewedTransactions,
      noRenewalTransactions,
      isSaving,
      isTemplateLoading,
      copiedId,
      sentMessageId,
      forceRefreshRenewalTemplate,
    };

  // Header renderer function
  const renderHeaderFunction = (column) => {
    if (column.id === 'select') {
      const isIndeterminate = selectedRows.size > 0 && selectedRows.size < filteredData.length;
      const isAllSelected = selectedRows.size > 0 && selectedRows.size === filteredData.length;
      
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <input
            type="checkbox"
            checked={isAllSelected}
            ref={(input) => {
              if (input) input.indeterminate = isIndeterminate;
            }}
            onChange={(e) => handleSelectAll(e.target.checked)}
            style={{
              width: "16px",
              height: "16px",
              accentColor: "#3b82f6", // Blue accent color
              cursor: "pointer",
            }}
          />
        </div>
      );
    }
    return column.label;
  };

  // Define the cell renderers using useMemo for stability and only recreating when necessary
  const renderCellFunction = useMemo(() => {
    console.log("Creating renderCellFunction");
    return (row, columnId) => {
      // Special handling for quick edit form rows
      if (row.isQuickEditRow) {
        // Only render content in the first visible column, make it span all columns
        if (
          columnId ===
          Object.keys(localVisibleColumns).find(
            (key) => localVisibleColumns[key]
          )
        ) {
        return (
            <td
              colSpan={
                Object.values(localVisibleColumns).filter(Boolean).length
              }
              style={{
                padding: 0,
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
              }}
            >
              <div
            style={{
                  padding: "16px",
                  backgroundColor: "#f9fafb",
                  borderTop: "1px dashed #e5e7eb",
                  borderBottom: "1px dashed #e5e7eb",
                  width: "100%",
                  position: "relative",
                  zIndex: 10,
                  minHeight: "100px",
                }}
              >
                <QuickEditForm
                  transactionId={row.originalTransactionId}
                  onSave={handlersRef.current.saveQuickEdit}
                  onCancel={handlersRef.current.cancelQuickEdit}
                  colspan={
                    Object.values(localVisibleColumns).filter(Boolean).length
                  }
                />
          </div>
            </td>
        );
        }
        // Skip rendering other cells in expanded row
        return null;
      }

      // Normal cell rendering for regular rows
      switch (columnId) {
      case "transactionId":
        const transactionLastMod = getLastModifiedForRecord(row.transactionId);
        return (
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{row.transactionId}</span>
            <ChangeIndicator 
              lastModified={transactionLastMod} 
              recentHours={24}
              style={{ marginLeft: 'auto' }}
            />
          </div>
        );

      case "clientName":
        return row.clientName;

      case "productName":
        return row.productName;

      case "price":
        return `EGP ${row.isGroupedRow ? row.price.toFixed(2) : row.price}`;

      case "expirationDays": {
          const days = handlersRef.current.calculateExpirationDays(
            row.date,
            row.subscriptionPeriod
          );

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {formatExpirationDays(days)}
            <div
              onClick={() => toggleSort("expirationDays")}
              style={{ cursor: "pointer" }}
            >
              {sortDirection.expirationDays === "ascending" ? (
                <ArrowUp size={14} />
              ) : (
                <ArrowDown size={14} />
              )}
            </div>
          </div>
        );
      }

      case "date":
        const dateLastMod = getLastModifiedForRecord(row.transactionId);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {row.date === "unknown" ? (
                <span style={styles.expiredBadge}>
                  <AlertCircle size={12} />
                  Unknown
                </span>
              ) : (
                row.date
              )}
              <div
                onClick={() => toggleSort("date")}
                style={{ cursor: "pointer" }}
              >
                {sortDirection.date === "ascending" ? (
                  <ArrowUp size={14} />
                ) : (
                  <ArrowDown size={14} />
                )}
              </div>
            </div>
            <LastModified 
              date={dateLastMod} 
              variant="compact"
              showIcon={true}
              style={{ fontSize: '10px', opacity: 0.7 }}
            />
          </div>
        );

      case "status":
        return getStatusBadge(row.status);

      case "notes":
        return row.notes ? (
          <button
            onClick={() => handlersRef.current.openNoteModal(row.notes)}
            style={styles.noteButton}
          >
            View Note
          </button>
        ) : null;

      case "actions":
        return !row.isGroupedRow ? (
          <div style={styles.actionButtons}>
              <style>
                {`
                  .edit-button:hover {
                    background-color: #ffc400 !important;
                  }
                  .delete-button:hover {
                    background-color: #dc2626 !important;
                  }
                  .history-button:hover {
                    background-color: #4f46e5 !important;
                  }
                `}
              </style>
            <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row);
                }}
              style={{
                ...styles.actionButton,
                ...styles.editButton,
              }}
                className="edit-button"
            >
              Edit
            </button>
            <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Navigate to logs page with pre-filtered transaction
                  navigate(`/home/logs?resourceId=${row.transactionId}&resource=sales`);
                }}
              style={{
                ...styles.actionButton,
                backgroundColor: '#6366f1',
                color: '#ffffff',
              }}
                className="history-button"
                title="View transaction history"
            >
              History
            </button>
            <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlersRef.current.handleDelete(row.transactionId);
                }}
              style={{
                ...styles.actionButton,
                ...styles.deleteButton,
              }}
                className="delete-button"
            >
              Delete
            </button>
          </div>
        ) : null;

      case "actions2": {
        if (row.isGroupedRow) return null;

          const days = calculateExpirationDays(
            row.date,
            row.subscriptionPeriod
          );
        const isExpired = typeof days === "number" && days <= 0;
        const isExpiringSoon =
          typeof days === "number" && days <= 35 && days > 0;
        const shouldShowCheckboxes = isExpired || isExpiringSoon;
        const isNoRenewal =
          handlersRef.current.noRenewalTransactions?.has(row.transactionId) || false;
        // Renewed via the Renew button (renewedTo) counts as renewed too
        const isRenewedViaButton = !!row.renewedTo;
        const isRenewed =
          handlersRef.current.renewedTransactions?.has(row.transactionId) ||
          isRenewedViaButton;

        return (
          <div className="action-buttons-container">
            <style>
              {`
                  .action-buttons-container {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                      flex-wrap: nowrap;
                      min-width: 600px;
                      overflow-x: auto;
                      padding-bottom: 4px;
                  }
                  .checkbox-container {
                    display: flex;
                    align-items: center;
                    padding: 2px 6px;
                    border-radius: 4px;
                    position: relative;
                    transition: all 0.2s ease;
                  }
                  .checkbox-container.saving {
                    opacity: 0.7;
                    pointer-events: none;
                  }
                  .checkbox-container::after {
                    content: '';
                    display: ${isSaving ? "block" : "none"};
                    position: absolute;
                    top: -4px;
                    right: -4px;
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background-color: #3b82f6;
                    animation: pulse 1.5s infinite;
                  }
                  @keyframes pulse {
                    0% { transform: scale(0.8); opacity: 0.5; }
                    50% { transform: scale(1.2); opacity: 1; }
                    100% { transform: scale(0.8); opacity: 0.5; }
                  }
                  .action-button {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                    padding: 6px 12px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    border: none;
                    height: 28px;
                    width: auto;
                    min-width: 60px;
                    white-space: nowrap;
                  }
                  .duplicate-button {
                    background-color: #8B5CF6;
                    color: white;
                  }
                  .duplicate-button:hover {
                    background-color: #7C3AED;
                  }
                  .renew-button {
                    background-color: #10B981;
                    color: white;
                  }
                  .renew-button:hover {
                    background-color: #059669;
                  }
                  .renew-button.expired {
                    animation: pulse 2s infinite;
                  }
                  .copy-button {
                    background-color: #0ea5e9;
                    color: white;
                  }
                  .copy-button:hover {
                    background-color: #0284c7;
                  }
                  .copy-button.copied {
                    background-color: #14b8a6;
                  }
                  .send-button {
                    background-color: #6366f1; /* Indigo color */
                    color: white;
                  }
                  .send-button:hover {
                    background-color: #4f46e5;
                  }
                  .send-button.sent {
                    background-color: #8b5cf6; /* Purple color */
                  }
                  .refresh-button {
                    background-color: #6B7280; /* Gray color */
                    color: white;
                    padding: 4px;
                    border-radius: 4px;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    margin-left: 4px;
                  }
                  .refresh-button:hover {
                    background-color: #4B5563;
                  }
                  .refresh-button.loading {
                    animation: spin 1s linear infinite;
                  }
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                  .save-status {
                    position: absolute;
                    top: -5px;
                    right: -5px;
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    border: 2px solid white;
                  }
                  .save-status.saved {
                    background-color: #22c55e;
                  }
                  .save-status.error {
                    background-color: #ef4444;
                  }
                  .save-status.saving {
                    background-color: #3b82f6;
                    animation: pulse 1.5s infinite;
                  }
                  .expire-message-button {
                    background-color: #ef4444;
                    color: white;
                  }
                  .expire-message-button:hover {
                    background-color: #dc2626;
                  }
                  .expire-message-button.sent {
                    background-color: #b91c1c;
                  }
                `}
            </style>

            <button
              onClick={() => handlersRef.current.handleDuplicate(row)}
              className="action-button duplicate-button"
              disabled={handlersRef.current.isSaving}
            >
              <Copy size={12} />
              Duplicate
            </button>

            <button
              onClick={() => handlersRef.current.handleRenew(row)}
              className={`action-button renew-button ${
                isExpired ? "expired" : ""
              }`}
              disabled={handlersRef.current.isSaving}
            >
              <RefreshCw size={12} />
              {isExpired ? "RENEW NOW!" : "Renew"}
            </button>

            <div style={{ position: "relative", display: "inline-block" }}>
              <button
                onClick={() => handlersRef.current.copyRenewalMessage(row)}
                className={`action-button copy-button ${
                  copiedId === row.transactionId ? "copied" : ""
                }`}
                disabled={isSaving || isTemplateLoading}
                style={{ position: "relative" }}
              >
                <Clipboard size={12} />
                {copiedId === row.transactionId ? "Copied!" : "Copy Message"}
                  <div
                  className={`refresh-button ${
                    isTemplateLoading ? "loading" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    forceRefreshRenewalTemplate();
                  }}
                  title="Force refresh template from server"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: "4px",
                      cursor: "pointer",
                    }}
                >
                  <RotateCcw size={12} />
                  </div>
              </button>
            </div>

            <button
              onClick={() => handlersRef.current.sendRenewalMessage(row)}
              className={`action-button send-button ${
                sentMessageId === row.transactionId ? "sent" : ""
              }`}
              disabled={isSaving || isTemplateLoading}
              title={`Send message via ${row.contact1Type || "WhatsApp"}`}
            >
              <MessageSquare size={12} />
              {sentMessageId === row.transactionId ? "Sent!" : "Send Message"}
            </button>

            {shouldShowCheckboxes && (
              <>
                <div
                  className={`checkbox-container no-renewal-checkbox ${
                    isSaving ? "saving" : ""
                  }`}
                  style={{
                    backgroundColor: isNoRenewal ? "#fef3c7" : "transparent",
                    border: `1px solid ${isNoRenewal ? "#fcd34d" : "transparent"}`,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isNoRenewal}
                    onChange={() => handlersRef.current.onNoRenewalToggle(row.transactionId)}
                    style={{ marginLeft: "8px" }}
                    disabled={isSaving}
                  />
                  <label
                    style={{
                      fontSize: "12px",
                      marginLeft: "4px",
                      fontWeight: "700",
                    }}
                  >
                    No Renewal
                  </label>
                </div>

                <div
                  className={`checkbox-container renewed-checkbox ${
                    isSaving ? "saving" : ""
                  }`}
                  style={{
                    backgroundColor: isRenewed ? "#dcfce7" : "transparent",
                    border: `1px solid ${isRenewed ? "#86efac" : "transparent"}`,
                  }}
                  title={
                    isRenewedViaButton
                      ? `Renewed via Renew button → ${row.renewedTo}`
                      : undefined
                  }
                >
                  <input
                    type="checkbox"
                    checked={isRenewed}
                    onChange={() => handlersRef.current.onRenewedToggle(row.transactionId)}
                    style={{ marginLeft: "8px" }}
                    disabled={isSaving || isRenewedViaButton}
                  />
                  <label
                    style={{
                      fontSize: "12px",
                      marginLeft: "4px",
                      fontWeight: "700",
                    }}
                  >
                    Renewed
                  </label>
                </div>
              </>
            )}

              {/* Add the new Expire Message button */}
              <button
                onClick={() => sendExpireMessage(row)}
                className={`action-button expire-message-button ${
                  sentMessageId === row.transactionId ? "sent" : ""
                }`}
                disabled={isSaving || isExpireTemplateLoading}
                title={`Send expire message via ${
                  row.contact1Type || "WhatsApp"
                }`}
              >
                <MessageSquare size={12} />
                {sentMessageId === row.transactionId ? "Sent!" : "Send Expire"}
              </button>
          </div>
        );
      }

      case "select":
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={selectedRows.has(row.transactionId)}
              onChange={() => handleSelectRow(row.transactionId)}
              onClick={(e) => e.stopPropagation()} // Prevent row click events
              style={{
                width: "16px",
                height: "16px",
                accentColor: "#3b82f6", // Blue accent color
                cursor: "pointer",
              }}
            />
          </div>
        );

      default:
        return row[columnId];
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Include selectedRows to ensure checkboxes update when selection changes
    localVisibleColumns,
    selectedRows,
  ]);

  // Edit popup component
  const EditPopup = () => {
    if (!showEditPopup || !editingRowId || !quickEditData[editingRowId])
      return null;

    const rowData = quickEditData[editingRowId];

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
        onClick={cancelPopupEdit}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "24px",
            width: "90%",
            maxWidth: "1000px",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            position: "relative",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#6b7280",
            }}
            onClick={cancelPopupEdit}
          >
            ×
          </button>

          <h2
            style={{
              fontSize: "20px",
              fontWeight: "600",
              marginBottom: "20px",
              color: "#4b5563",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Edit Sale: {rowData.transactionId}</span>
          </h2>

          {/* Embed the QuickEditForm inside the popup */}
          <div style={{ padding: "16px 0" }}>
            <QuickEditFormContent
              transactionId={editingRowId}
              onSave={savePopupEdit}
              onCancel={cancelPopupEdit}
            />
          </div>
        </div>
      </div>
    );
  };

  // Separate QuickEditFormContent component that can be used in both popup and inline forms
  const QuickEditFormContent = ({ transactionId, onSave, onCancel }) => {
    const rowData = quickEditData[transactionId];

    // Declare all local state variables
    const [localNotes, setLocalNotes] = useState("");
    const [localClientName, setLocalClientName] = useState("");
    const [localClientEmail, setLocalClientEmail] = useState("");
    const [localEmailEdu, setLocalEmailEdu] = useState("");
    const [localContact1, setLocalContact1] = useState("");
    const [localContact1Type, setLocalContact1Type] = useState("Whatsapp");
    const [localContact2, setLocalContact2] = useState("");
    const [localContact2Type, setLocalContact2Type] = useState("Whatsapp");
    const [localProductName, setLocalProductName] = useState("");
    const [localProductVariant, setLocalProductVariant] = useState("");
    const [localPrice, setLocalPrice] = useState("");
    const [localAmount, setLocalAmount] = useState("");
    const [localPaymentMethod, setLocalPaymentMethod] = useState("instaPay");
    const [localDate, setLocalDate] = useState("");
    const [localStatus, setLocalStatus] = useState("OnHold");
    const [localSubscriptionPeriod, setLocalSubscriptionPeriod] =
      useState("1 month");
    const [localUnknownDate, setLocalUnknownDate] = useState(false);

    // Initialize local state when rowData changes
  useEffect(() => {
      if (rowData) {
        setLocalNotes(rowData.notes || "");
        setLocalClientName(rowData.clientName || "");
        setLocalClientEmail(rowData.clientEmail || "");
        setLocalEmailEdu(rowData.emailEdu || "");
        setLocalContact1(rowData.contact1 || "");
        setLocalContact1Type(rowData.contact1Type || "Whatsapp");
        setLocalContact2(rowData.contact2 || "");
        setLocalContact2Type(rowData.contact2Type || "Whatsapp");
        setLocalProductName(rowData.productName || "");
        setLocalProductVariant(rowData.productVariant || "");
        setLocalPrice(rowData.price || "");
        setLocalAmount(rowData.amount || "");
        setLocalPaymentMethod(rowData.paymentMethod || "instaPay");
        setLocalDate(rowData.date || "");
        setLocalStatus(rowData.status || "OnHold");
        setLocalSubscriptionPeriod(rowData.subscriptionPeriod || "1 month");
        setLocalUnknownDate(rowData.date === "unknown");
      }
    }, [rowData]);

    if (!rowData) return null;

    // Generic change handler for all fields
    const handleFieldChange = (field, value, stateUpdater) => {
      stateUpdater(value);
    };

    // Generic blur handler for all fields
    const handleFieldBlur = (field, value) => {
      handleQuickEditChange(transactionId, field, value);
    };

    return (
      <div style={{ padding: "16px" }}>
        {/* Client Information */}
        <div style={{ marginBottom: "24px" }}>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Client Information
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Client Name
              </label>
              <input
                type="text"
                value={localClientName}
                onChange={(e) =>
                  handleFieldChange(
                    "clientName",
                    e.target.value,
                    setLocalClientName
                  )
                }
                onBlur={() => handleFieldBlur("clientName", localClientName)}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Client Email
              </label>
              <input
                type="email"
                value={localClientEmail}
                onChange={(e) =>
                  handleFieldChange(
                    "clientEmail",
                    e.target.value,
                    setLocalClientEmail
                  )
                }
                onBlur={() => handleFieldBlur("clientEmail", localClientEmail)}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Edu Email
              </label>
              <input
                type="email"
                value={localEmailEdu}
                onChange={(e) =>
                  handleFieldChange(
                    "emailEdu",
                    e.target.value,
                    setLocalEmailEdu
                  )
                }
                onBlur={() => handleFieldBlur("emailEdu", localEmailEdu)}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div style={{ marginBottom: "24px" }}>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Contact Information
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ flex: "3" }}>
                <label style={{ display: "block", marginBottom: "8px" }}>
                  Contact 1
                </label>
                <input
                  type="text"
                  value={localContact1}
                  onChange={(e) =>
                    handleFieldChange(
                      "contact1",
                      e.target.value,
                      setLocalContact1
                    )
                  }
                  onBlur={() => handleFieldBlur("contact1", localContact1)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>
              <div style={{ flex: "2" }}>
                <label style={{ display: "block", marginBottom: "8px" }}>
                  Type
                </label>
                <select
                  value={localContact1Type}
                  onChange={(e) =>
                    handleFieldChange(
                      "contact1Type",
                      e.target.value,
                      setLocalContact1Type
                    )
                  }
                  onBlur={() =>
                    handleFieldBlur("contact1Type", localContact1Type)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                >
                  <option value="Whatsapp">Whatsapp</option>
                  <option value="Telegram">Telegram</option>
                  <option value="Messenger">Messenger</option>
                  <option value="Email">Email</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ flex: "3" }}>
                <label style={{ display: "block", marginBottom: "8px" }}>
                  Contact 2
                </label>
                <input
                  type="text"
                  value={localContact2}
                  onChange={(e) =>
                    handleFieldChange(
                      "contact2",
                      e.target.value,
                      setLocalContact2
                    )
                  }
                  onBlur={() => handleFieldBlur("contact2", localContact2)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>
              <div style={{ flex: "2" }}>
                <label style={{ display: "block", marginBottom: "8px" }}>
                  Type
                </label>
                <select
                  value={localContact2Type}
                  onChange={(e) =>
                    handleFieldChange(
                      "contact2Type",
                      e.target.value,
                      setLocalContact2Type
                    )
                  }
                  onBlur={() =>
                    handleFieldBlur("contact2Type", localContact2Type)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                >
                  <option value="Whatsapp">Whatsapp</option>
                  <option value="Telegram">Telegram</option>
                  <option value="Messenger">Messenger</option>
                  <option value="Email">Email</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Product and Payment Details */}
        <div style={{ marginBottom: "24px" }}>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Product & Payment Details
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Product
              </label>
              <select
                value={localProductName}
                onChange={(e) => {
                  const newProductName = e.target.value;
                  setLocalProductName(newProductName);
                  const product = products.find(
                    (p) => p.name === newProductName
                  );
                  if (product && product.price) {
                    setLocalPrice(product.price.toString());
                    handleQuickEditChange(
                      transactionId,
                      "price",
                      product.price.toString()
                    );
                  }
                  handleQuickEditChange(
                    transactionId,
                    "productName",
                    newProductName
                  );
                }}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id || product.name} value={product.name}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            {localProductName === "Autodesk" && (
              <div>
                <label style={{ display: "block", marginBottom: "8px" }}>
                  Autodesk Variant
                </label>
                <input
                  type="text"
                  value={localProductVariant}
                  onChange={(e) =>
                    handleFieldChange(
                      "productVariant",
                      e.target.value,
                      setLocalProductVariant
                    )
                  }
                  onBlur={() =>
                    handleFieldBlur("productVariant", localProductVariant)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>
            )}

            <div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Price (EGP)
              </label>
              <input
                type="number"
                value={localPrice}
                onChange={(e) =>
                  handleFieldChange("price", e.target.value, setLocalPrice)
                }
                onBlur={() => handleFieldBlur("price", localPrice)}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Amount
              </label>
              <input
                type="number"
                value={localAmount}
                onChange={(e) =>
                  handleFieldChange("amount", e.target.value, setLocalAmount)
                }
                onBlur={() => handleFieldBlur("amount", localAmount)}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Payment Method
              </label>
              <select
                value={localPaymentMethod}
                onChange={(e) =>
                  handleFieldChange(
                    "paymentMethod",
                    e.target.value,
                    setLocalPaymentMethod
                  )
                }
                onBlur={() =>
                  handleFieldBlur("paymentMethod", localPaymentMethod)
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                <option value="instaPay">InstaPay</option>
                <option value="Vfcash">Vfcash</option>
                <option value="Binance">Binance</option>
                <option value="Gate.io">Gate.io</option>
                <option value="Paypal">Paypal</option>
                <option value="Cash">Cash</option>
                <option value="N/A">N/A</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        <div style={{ marginBottom: "24px" }}>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Subscription Details
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Date
              </label>
              <div style={{ marginBottom: "8px" }}>
                <input
                  type="date"
                  value={localUnknownDate ? "" : localDate}
                  onChange={(e) =>
                    handleFieldChange("date", e.target.value, setLocalDate)
                  }
                  onBlur={() => handleFieldBlur("date", localDate)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                  disabled={localUnknownDate}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#fff3e0",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              >
                <input
                  type="checkbox"
                  checked={localUnknownDate}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setLocalUnknownDate(checked);
                    handleFieldBlur("date", checked ? "unknown" : localDate);
                  }}
                  style={{ marginRight: "8px" }}
                />
                <label style={{ fontSize: "14px", color: "#ed6c02" }}>
                  Unknown Date
                </label>
              </div>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Status
              </label>
              <select
                value={localStatus}
                onChange={(e) =>
                  handleFieldChange("status", e.target.value, setLocalStatus)
                }
                onBlur={() => handleFieldBlur("status", localStatus)}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                <option value="OnHold">OnHold</option>
                <option value="Processing">Processing</option>
                <option value="Completed">Completed</option>
                <option value="Refunded">Refunded</option>
                <option value="Expired">Expired</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Subscription Period
              </label>
              <select
                value={localSubscriptionPeriod}
                onChange={(e) =>
                  handleFieldChange(
                    "subscriptionPeriod",
                    e.target.value,
                    setLocalSubscriptionPeriod
                  )
                }
                onBlur={() =>
                  handleFieldBlur("subscriptionPeriod", localSubscriptionPeriod)
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                <option value="1 month">1 month</option>
                <option value="3 months">3 months</option>
                <option value="6 months">6 months</option>
                <option value="12 months">12 months</option>
                <option value="24 months">24 months</option>
                <option value="36 months">36 months</option>
                <option value="one-time">One-time purchase</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: "24px" }}>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Notes
          </h4>
          <textarea
            value={localNotes}
            onChange={(e) =>
              handleFieldChange("notes", e.target.value, setLocalNotes)
            }
            onBlur={() => handleFieldBlur("notes", localNotes)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              minHeight: "100px",
              resize: "vertical",
            }}
          />
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: "24px",
            paddingTop: "16px",
            borderTop: "1px solid #eee",
          }}
        >
          <button
            onClick={() => onCancel(transactionId)}
            style={{
              padding: "8px 16px",
              backgroundColor: "#f3f4f6",
              color: "#374151",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={() => onSave(transactionId)}
            style={{
              padding: "8px 16px",
              backgroundColor: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Save size={16} />
            Save
          </button>
        </div>
      </div>
    );
  };

  // Add this new function to get expire template
  const getExpireTemplate = async () => {
    try {
      setIsExpireTemplateLoading(true);
      console.log("Fetching expire template from Firestore...");

      // First try to get from localStorage
      const cachedTemplate = localStorage.getItem("expire_message_template");
      const cachedTimestamp = localStorage.getItem(
        "expire_message_template_timestamp"
      );

      if (cachedTemplate && cachedTimestamp) {
        console.log("Found cached expire template:", cachedTemplate);
        return cachedTemplate;
      }

      // If no cache or cache is old, fetch from Firestore
      const messageRef = doc(db, "Settings", "expireMessageTemplate");
      const messageDoc = await getDoc(messageRef);

      if (messageDoc.exists()) {
        const data = messageDoc.data();
        console.log("Loaded expire template from Firestore:", data.message);

        // Update cache
        localStorage.setItem("expire_message_template", data.message);
        localStorage.setItem(
          "expire_message_template_timestamp",
          data.version?.toString() || Date.now().toString()
        );

        return data.message;
      } else {
        console.log("No expire template found in Firestore, using default");
        return null;
      }
    } catch (error) {
      console.error("Error fetching expire template:", error);
      return null;
    } finally {
      setIsExpireTemplateLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const forceRefreshExpireTemplate = async () => {
    try {
      setIsExpireTemplateLoading(true);

      // Clear local storage cache
      localStorage.removeItem("expire_message_template");
      localStorage.removeItem("expire_message_template_timestamp");

      // Fetch fresh from Firestore
      const messageRef = doc(db, "Settings", "expireMessageTemplate");
      const messageDoc = await getDoc(messageRef, { source: "server" });

      if (messageDoc.exists()) {
        const data = messageDoc.data();
        localStorage.setItem("expire_message_template", data.message);
        localStorage.setItem(
          "expire_message_template_timestamp",
          data.version?.toString() || Date.now().toString()
        );
        return data.message;
      }

      return null;
    } catch (error) {
      console.error("Error force refreshing expire template:", error);
      return null;
    } finally {
      setIsExpireTemplateLoading(false);
    }
  };

  // Update the sendExpireMessage function
  const sendExpireMessage = async (sale) => {
    try {
      setSentMessageId(null); // Reset sent status
      console.log("🚀 Preparing to send expire message for:", sale.clientName);

      // Get the expire template
      const freshTemplate = await getExpireTemplate();
      console.log("Fresh expire template for sending:", freshTemplate);

      if (!freshTemplate) {
        console.log("Using default expire template");
      }

      // Process the template with sale data
      let message = await processTemplate(freshTemplate, sale);

      if (!message || message.trim() === "") {
        console.error("Cannot send empty message");
        alert(
          "Error: Generated message is empty. Please check the expire template in Settings."
        );
        return;
      }

      // Check for contact info
      const contact1 = sale.contact1;
      const contact1Type = sale.contact1Type || "Whatsapp";
      const contact2 = sale.contact2;
      const contact2Type = sale.contact2Type || "Whatsapp";

      // For demo purposes, prefer contact1 if available
      let contactToUse = contact1;
      let contactTypeToUse = contact1Type;

      if (!contactToUse && contact2) {
        contactToUse = contact2;
        contactTypeToUse = contact2Type;
      }

      if (!contactToUse) {
        alert("No contact information found for this client.");
        return;
      }

      const encodedMessage = encodeURIComponent(message);
      let url = "";

      // Format phone numbers - remove non-numeric characters
      const formatPhone = (phone) => {
        const cleaned = phone
          .replace(/^(\+)/, "PLUSSIGN")
          .replace(/\D/g, "")
          .replace("PLUSSIGN", "+");

        if (!cleaned.startsWith("+")) {
          if (cleaned.startsWith("0")) {
            return "+2" + cleaned;
          } else if (cleaned.startsWith("2")) {
            return "+" + cleaned;
          } else {
            return "+20" + cleaned;
          }
        }
        return cleaned;
      };

      // Create URL based on platform
      switch (contactTypeToUse.toLowerCase()) {
        case "whatsapp":
          const phoneForWhatsApp = formatPhone(contactToUse).replace("+", "");
          url = `whatsapp://send?phone=${phoneForWhatsApp}&text=${encodedMessage}`;
          break;

        case "telegram":
          try {
            await navigator.clipboard.writeText(message);
            setSentMessageId(sale.transactionId);

            const isPhoneNumber = /^\+?\d+$/.test(contactToUse);
            const hasUsername =
              contactToUse.startsWith("@") ||
              (!isPhoneNumber && !contactToUse.includes(" "));

            if (!isPhoneNumber && !hasUsername) {
              alert(
                `Message copied for Telegram contact: ${sale.clientName}\n\n` +
                  "IMPORTANT: This contact only has a display name.\n" +
                  "Please:\n" +
                  "1. Open Telegram manually\n" +
                  "2. Find the contact: " +
                  contactToUse +
                  "\n" +
                  "3. Paste the message (Ctrl+V)"
              );
              return;
            }

            let telegramContact = contactToUse;
            if (isPhoneNumber) {
              telegramContact = formatPhone(telegramContact).replace("+", "");
              url = `tg://resolve?phone=${telegramContact}`;
            } else {
              telegramContact = telegramContact.replace(/^@/, "");
              url = `tg://resolve?domain=${telegramContact}`;
            }

            const telegramWindow = window.open(url, "_blank");
            if (
              !telegramWindow ||
              telegramWindow.closed ||
              typeof telegramWindow.closed === "undefined"
            ) {
              setTimeout(() => {
                window.open(`https://t.me/${telegramContact}`, "_blank");
              }, 300);
            }

            setTimeout(() => {
              alert(
                "Attempting to open Telegram...\n\n" +
                  "IMPORTANT: Message copied to clipboard!\n\n" +
                  "If Telegram opens:\n" +
                  "1. Click in the message field\n" +
                  "2. Paste the message (Ctrl+V)\n\n" +
                  "If Telegram doesn't open:\n" +
                  "1. Open Telegram manually\n" +
                  "2. Find contact: " +
                  contactToUse +
                  "\n" +
                  "3. Paste the message"
              );
            }, 1000);
            return;
          } catch (err) {
            console.error("Failed with Telegram:", err);
            alert(
              "Message copied to clipboard.\n\n" +
                "Please:\n" +
                "1. Open Telegram manually\n" +
                "2. Find contact: " +
                contactToUse +
                "\n" +
                "3. Paste the message (Ctrl+V)"
            );
            return;
          }

        case "messenger":
          try {
            await navigator.clipboard.writeText(message);
            setSentMessageId(sale.transactionId);
            setTimeout(() => setSentMessageId(null), 2000);

            const clipboardSearchHelper = `Message for: ${sale.clientName}\n\n${message}`;
            await navigator.clipboard.writeText(clipboardSearchHelper);

            if (facebookBusinessUrl && facebookBusinessUrl.trim() !== "") {
              window.open(facebookBusinessUrl, "_blank");
            } else {
              window.open(
                "https://business.facebook.com/latest/inbox",
                "_blank"
              );
            }

            alert(
              "Message copied to clipboard!\n\n" +
                "Please:\n" +
                "1. Find the conversation with: " +
                sale.clientName +
                "\n" +
                "2. Paste the message (Ctrl+V)"
            );
            return;
          } catch (err) {
            console.error("Failed with Messenger:", err);
            alert(
              "Message copied to clipboard.\n\n" +
                "Please open Messenger manually, find " +
                sale.clientName +
                ", and paste the message."
            );
            return;
          }

        default:
          alert(`Unsupported contact type: ${contactTypeToUse}`);
          return;
      }

      // Try to open the URL for other platforms (mainly WhatsApp)
      try {
        setSentMessageId(sale.transactionId);
        setTimeout(() => setSentMessageId(null), 2000);

        window.open(url, "_blank");
        console.log(
          `Sent expire message via ${contactTypeToUse} to ${contactToUse}`
        );
      } catch (error) {
        console.error(`Error sending message via ${contactTypeToUse}:`, error);
        alert(
          `Failed to send message via ${contactTypeToUse}. Please try copying and pasting manually.`
        );
      }
    } catch (error) {
      console.error("Error in sendExpireMessage:", error);
      alert(`Error preparing message: ${error.message}`);
    }
  };

  return (
    <div className="table-responsive overflow-x-auto">
      <style>
        {`
        /* Custom CSS for tables with dark mode support */
        @media screen and (max-width: 768px) {
          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
        }
        
        /* Quick edit form styles */
        .quick-edit-form {
          transition: all 0.3s ease-in-out;
          overflow: visible;
        }

        /* Fix for quick edit form in ResizableTable */
        td[colspan] {
          background-color: transparent !important;
          border: none !important;
          padding: 0 !important;
          box-shadow: none !important;
        }
        
        td[colspan] > div {
          width: 100%;
          background-color: #f9fafb;
          position: relative;
          z-index: 10;
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin: 8px 0;
        }
        `}
      </style>
      
      {/* Bulk Action Toolbar */}
      {selectedRows.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Check className="h-5 w-5 text-blue-600" />
              <span className="text-blue-700 font-medium">
                {selectedRows.size} record{selectedRows.size > 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setSelectedRows(new Set())}
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                Clear selection
              </button>
            </div>
            <button
              onClick={() => setShowBulkEditModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
            >
              <Edit3 className="h-4 w-4" />
              Bulk Edit
            </button>
          </div>
        </div>
      )}
      
      <div className={isDarkMode ? "dark-mode" : ""}>
        <ResizableTable
          columns={updatedColumns}
          data={expandedData}
          visibleColumns={localVisibleColumns}
          renderCell={renderCellFunction}
          renderHeader={renderHeaderFunction}
          getRowStyle={getRowStyleWithExpansion}
        />

        {/* Render the edit popup */}
        {showEditPopup && editingRowId && <EditPopup />}
        
        {/* Bulk Edit Modal */}
        <BulkEditModal
          isOpen={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          selectedRows={Array.from(selectedRows)}
          selectedRecord={
            selectedRows.size === 1
              ? data.find(row => row.transactionId === Array.from(selectedRows)[0])
              : null
          }
          onBulkUpdate={handleBulkUpdateInternal}
          isUpdating={isBulkUpdating}
          products={products}
        />
      </div>
    </div>
  );
};

export default React.memo(SalesTable);
